import { ExistingSalaryComponents } from 'components/Fnf/types';
import { PayrollAllowanceV2 } from 'components/RunPayroll/Popups/EditSalary/types';
import {
  isAdditionAdhocPayment,
  isDeductionAdhocPayment,
  isDeductionRecurring,
} from 'components/RunPayroll/Popups/EditSalary/utils';
import { RunPayrollDeductionV2 } from 'components/RunPayroll/utils/types';
import { format } from 'date-fns';
import { gql } from 'graphql-request';
import { FnfUpdateResponseSchemaContract } from 'schemas/FnfUpdateResponseSchema';
import { GetFnfResponseSchemaContract } from 'schemas/GetFnfResponseSchema';
import { graphQLApi } from 'utils/Api';
import { dateFormats } from 'utils/Dates';
import { rupeeAmountToPaise } from 'utils/Numbers';
import {
  ComponentAssignmentRequestInput,
  LopData,
  PayrollUpdateFnfDataResponse,
  PayrollUpdateFnfDataVariables,
} from '../types';
import { isM3S1Disabled } from 'access/utils';

const updateFnfMutation = gql`
  mutation PayrollUpdateFnfData(
    $deactivateDate: String!
    $additions: [ComponentAssignmentRequestInput!]!
    $deductions: [ComponentAssignmentRequestInput!]!
    $lopData: LopDaysUpdateInput!
    $userData: PayrollUserUpdateInput!
  ) {
    payrollUpdateFnfData(
      deactivateDate: $deactivateDate
      additions: $additions
      deductions: $deductions
      lopData: $lopData
      userData: $userData
    ) {
      code
      success
      message
      data {
        success
        payslip_link
        payroll_month
        runpayroll_link
        message_type
      }
    }
  }
`;

const mapAdditions = (
  additions: Exclude<GetFnfResponseSchemaContract['additions'], null> = [],
  isM3S1Disabled = false,
): ComponentAssignmentRequestInput[] => {
  return additions
    .filter((comp) => comp.id && parseInt(rupeeAmountToPaise(comp.amount).toString()) > 0)
    .filter((comp) => !isAdditionAdhocPayment(comp as PayrollAllowanceV2))
    .map((component) => ({
      componentId: component.id as string,
      value: rupeeAmountToPaise(component.amount).toString(),
      includeInCompensation: false,
      includeInPayroll: true,

      ...(!isM3S1Disabled
        ? {
            payType:
              component?.payType ||
              component?.componentDetails?.settings?.earningDetails?.payType ||
              '',
          }
        : {}),
    }));
};

const mapDeductions = (
  deductions: Exclude<GetFnfResponseSchemaContract['deductions']['non_lop_deductions'], null> = [],
): ComponentAssignmentRequestInput[] => {
  return deductions
    .filter((comp) => comp.id && parseInt(rupeeAmountToPaise(comp.amount).toString()) > 0)
    .filter(
      (comp) =>
        !isDeductionAdhocPayment(comp as RunPayrollDeductionV2) &&
        !isDeductionRecurring(comp as RunPayrollDeductionV2),
    )
    .map((component) => ({
      componentId: component.id as string,
      value: rupeeAmountToPaise(component.amount).toString(),
      includeInCompensation: false,
      includeInPayroll: true,
    }));
};

const mapLopData = (lopDays: string = '0', peopleId: string, payrollMonth: string): LopData => ({
  data: [
    {
      peopleId: parseInt(peopleId),
      lopDays: parseInt(lopDays),
    },
  ],
  payrollMonth,
  source: 'fnf',
});

const mapUserData = (
  userId: string,
  deactivateDate: string,
  reasonForLeaving?: string | null,
  personalEmailAddress?: string | null,
) => ({
  userId,
  deactivateDate,
  reasonForLeaving,
  personalEmailAddress,
});

export const transformToGraphQLVariables = (
  fnfData: GetFnfResponseSchemaContract,
  dismissalDate: Date,
  peopleId: string,
  existingComponents: ExistingSalaryComponents,
  isM3S1Disabled = false,
): PayrollUpdateFnfDataVariables => {
  const effectiveFrom = format(dismissalDate, dateFormats.yearMonthDate);
  const existingDeductions = existingComponents.deductions || [];
  const existingAdditions = existingComponents.additions || [];

  const deletedAdditionIds = existingAdditions
    .filter((comp) => comp.server_id)
    .filter((comp) => !fnfData.additions?.some((c) => c.id === comp.id) && comp.id)
    .filter((comp) => !isAdditionAdhocPayment(comp as PayrollAllowanceV2))
    .map((comp) => comp.server_id as string);

  const deletedDeductionIds = existingDeductions
    .filter((comp) => comp.server_id)
    .filter(
      (comp) => !fnfData.deductions.non_lop_deductions?.some((c) => c.id === comp.id) && comp.id,
    )
    .filter(
      (comp) =>
        !isDeductionAdhocPayment(comp as RunPayrollDeductionV2) &&
        !isDeductionRecurring(comp as RunPayrollDeductionV2),
    )
    .map((comp) => comp.server_id as string);
  const additions = mapAdditions(fnfData.additions || [], isM3S1Disabled);
  const deductions = mapDeductions(fnfData.deductions.non_lop_deductions || []);
  const gratuityAmount = fnfData.gratuity?.gratuity_amount ?? undefined;
  const gratuityValue =
    gratuityAmount !== undefined && gratuityAmount != null ? gratuityAmount.toString() : undefined;
  const leaveEncashmentAmount = fnfData.leave_encashments?.total_encashment ?? undefined;
  const leaveEncashmentValue =
    leaveEncashmentAmount !== undefined && leaveEncashmentAmount != null
      ? leaveEncashmentAmount.toString()
      : undefined;

  return {
    deactivateDate: effectiveFrom,
    additions,
    deductions,
    lopData: mapLopData(fnfData.deductions.lop_deduction?.lop_days, peopleId, effectiveFrom),
    userData: mapUserData(
      peopleId,
      effectiveFrom,
      fnfData.reason_for_leaving,
      fnfData.personal_email_address,
    ),
    fnfConfig: {
      gratuityAmount: gratuityValue,
      leaveEncashmentDays: fnfData.leave_encashments?.leave_days_encashed ?? undefined,
      leaveEncashmentAmount: leaveEncashmentValue,
    },
    deletedAdditionIds,
    deletedDeductionIds,
  };
};

export const updateFnfDetails = async (
  fnfData: GetFnfResponseSchemaContract,
  dismissalDate: Date,
  peopleId: string,
  existingComponents: ExistingSalaryComponents,
  isM3S1Disabled: boolean = false,
): Promise<FnfUpdateResponseSchemaContract> => {
  try {
    const queryVariables = transformToGraphQLVariables(
      fnfData,
      dismissalDate,
      peopleId,
      existingComponents,
      isM3S1Disabled,
    );

    const response = await graphQLApi<PayrollUpdateFnfDataResponse, PayrollUpdateFnfDataVariables>({
      query: updateFnfMutation,
      queryVariables,
    });

    return response.payrollUpdateFnfData.data;
  } catch (error: unknown) {
    throw error;
  }
};
